import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, ShieldCheck } from 'lucide-react';
import toast from '../utils/toast';
import DashboardLayout from '../components/Layout/DashboardLayout';
import { talentAssessmentsAPI } from '../services/api';
import { formatDateTime } from '../utils/dateUtils';

/**
 * Big Five Profile — access log (transparency view).
 *
 * Shows the employee every HR Admin who has viewed their individual Big
 * Five result, when, and the justification they recorded.
 */
const JUSTIFICATION_LABELS = {
  talent_development: 'Talent development',
  coaching: 'Coaching',
  role_fit: 'Role-fit discussion',
  succession_planning: 'Succession planning',
  performance_review_support: 'Performance review support',
  employee_requested_discussion: 'You requested this discussion',
  other: 'Other',
};

const SelfServiceBigFiveAccessLog = () => {
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await talentAssessmentsAPI.getMyBigFiveAccessLog();
      setEntries(data?.entries || []);
    } catch (err) {
      toast.error('Could not load your access log.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout>
      <div className="space-y-6 md:space-y-8 bg-gray-50 min-h-screen pb-24">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
          <div className="flex items-center gap-4">
            <Link
              to="/self-service/big-five"
              aria-label="Back to Big Five Profile"
              title="Back to Big Five Profile"
              className="icon-button flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="min-w-0">
              <h1 className="text-subsection-header truncate">Who has viewed your Big Five Profile</h1>
              <p className="text-body mt-1">
                Every access by HR, HOD, Deputy HOD, or your Reporting Officer is recorded here. They cannot view your profile without giving a reason.
              </p>
            </div>
          </div>
        </div>

        {/* Reassurance banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 sm:p-5 flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-blue-800 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            HR, your HOD, Deputy HOD, and Reporting Officer can view your individual Big Five Profile — each access requires a justification recorded here. Trade Leaders and other managers cannot see it unless they also hold one of those roles.
          </div>
        </div>

        {/* List */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {loading ? (
            <div className="p-10 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-blue-800 animate-spin" />
            </div>
          ) : entries.length === 0 ? (
            <div className="p-8 sm:p-12 text-center">
              <ShieldCheck className="w-10 h-10 text-emerald-600 mx-auto" />
              <div className="mt-3 text-base font-semibold text-gray-900">No views yet</div>
              <p className="mt-1 text-sm text-gray-600">No one in HR has viewed your Big Five Profile.</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {entries.map((e) => (
                <li key={e.id} className="p-4 sm:p-5">
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-900">
                        {e.viewer_display_name || e.viewer_staff_id || 'HR'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatDateTime(e.accessed_at)}
                      </div>
                    </div>
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-blue-50 text-blue-800 text-xs font-medium">
                      {JUSTIFICATION_LABELS[e.justification_code] || e.justification_code}
                    </span>
                  </div>
                  {e.justification_text && (
                    <div className="mt-2 text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
                      {e.justification_text}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SelfServiceBigFiveAccessLog;
